import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';

export type EntityVisualSize = 'sm' | 'md';

@Component({
  selector: 'zag-entity-visual',
  imports: [ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (imageUrl()) {
      <img class="zag-entity-visual__image" [src]="imageUrl()!" alt="" aria-hidden="true" />
    } @else if (icon()) {
      <span
        class="zag-entity-visual__icon"
        [class.zag-entity-visual__icon--surface]="hasColoredSurface()"
        [class.zag-icon-surface-gradient]="hasColoredSurface()"
        [style.background-color]="surfaceColor()"
        [style.color]="iconColor()"
        [zagIcon]="icon()!"
        aria-hidden="true"
      ></span>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      :host(.zag-entity-visual--sm) {
        width: var(--entity-visual-size-sm);
        height: var(--entity-visual-size-sm);
      }

      :host(.zag-entity-visual--md) {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
      }

      .zag-entity-visual__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: var(--border-radius-sm);
        border: 1px solid var(--border-color-default);
      }

      .zag-entity-visual__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        color: var(--text-color-secondary);
      }

      .zag-entity-visual__icon--surface {
        border-radius: 50%;
        background-color: var(--color-primary);
      }

      :host(.zag-entity-visual--sm) .zag-entity-visual__icon {
        font-size: var(--icon-size-secondary);
      }

      :host(.zag-entity-visual--sm) .zag-entity-visual__icon--surface {
        font-size: var(--icon-size-compact);
      }

      :host(.zag-entity-visual--md) .zag-entity-visual__icon {
        font-size: var(--entity-avatar-icon-size-list);
      }
    `,
  ],
  host: {
    '[class.zag-entity-visual--sm]': 'size() === "sm"',
    '[class.zag-entity-visual--md]': 'size() === "md"',
  },
})
export class EntityVisualComponent {
  private readonly colorContrast = inject(ColorContrastService);

  readonly icon = input<string | null | undefined>(undefined);
  readonly color = input<string | null | undefined>(undefined);
  readonly imageUrl = input<string | null | undefined>(undefined);
  readonly size = input<EntityVisualSize>('sm');

  protected readonly hasColoredSurface = computed(() => !!this.icon());
  protected readonly surfaceColor = computed(() => this.color() ?? null);
  protected readonly iconColor = computed(() => (this.icon() ? this.colorContrast.getIconColor(this.color()) : null));
}
